// src/components/legal/LegalAcceptanceModal.tsx
// Enterprise-grade, non-dismissible legal acceptance modal.
//
// Key UX features:
//   - Cannot be closed, no backdrop click dismiss, no escape key
//   - User must scroll to the bottom of the document before "I Accept" is enabled
//   - Progress indicator for multiple pending documents
//   - Dark-glass surface, consistent with /environments, /billing and
//     /superadmin. See cursor_logs/069 for the ink rules on this surface.
//   - Markdown content rendered as formatted HTML

import {
  CheckCircle as AcceptedIcon,
  Gavel as LegalIcon,
  KeyboardArrowDown as ScrollIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { LegalDocument, useLegalCompliance } from "hooks/useLegalCompliance";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "../../i18n";
import { athenaColors } from "../../theme/palette";
import { glass } from "../../theme/glassTokens";

// ---------------------------------------------------------------------------
// Markdown-to-HTML renderer (lightweight — handles ##, ###, bold, lists, paragraphs)
// ---------------------------------------------------------------------------

function renderMarkdownToHtml(md: string): string {
  return (
    md
      // Headings: ### h3, ## h2
      .replace(
        /^### (.+)$/gm,
        '<h3 style="margin:24px 0 8px;font-size:1.05rem;font-weight:600;letter-spacing:0.02em">$1</h3>'
      )
      .replace(
        /^## (.+)$/gm,
        '<h2 style="margin:32px 0 12px;font-size:1.25rem;font-weight:700;letter-spacing:0.01em">$1</h2>'
      )
      // Bold
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
      // Italic
      .replace(/\*(.+?)\*/g, "<em>$1</em>")
      // Unordered list items (top-level)
      .replace(
        /^- (.+)$/gm,
        '<li style="margin-bottom:4px;line-height:1.7">$1</li>'
      )
      // Wrap consecutive <li> items in <ul>
      .replace(
        /(<li[^>]*>.*?<\/li>\n?)+/g,
        '<ul style="padding-left:24px;margin:8px 0">$&</ul>'
      )
      // Paragraphs: any line that isn't already wrapped in an HTML tag
      .replace(
        /^(?!<[a-z])([\w\d].+)$/gm,
        '<p style="margin:8px 0;line-height:1.75">$1</p>'
      )
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function LegalAcceptanceModal() {
  const theme = useTheme();
  const { t } = useTranslation();
  const {
    pendingDocuments,
    loading,
    hasPendingLegal,
    acceptDocument,
    accepting,
  } = useLegalCompliance();

  // Track which doc we're currently showing
  const [currentIdx, setCurrentIdx] = useState(0);
  // Whether user has scrolled to bottom of the current document
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  // Ref for the scrollable content area
  const contentRef = useRef<HTMLDivElement>(null);
  // Track accepted count for the progress animation
  const [acceptedCount, setAcceptedCount] = useState(0);

  const totalDocuments = pendingDocuments.length + acceptedCount;
  const currentDoc: LegalDocument | undefined = pendingDocuments[currentIdx];

  // -----------------------------------------------------------------------
  // Scroll detection
  // -----------------------------------------------------------------------

  const handleScroll = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    // Allow ~30px tolerance for scroll rounding
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 30;
    if (isAtBottom) {
      setHasScrolledToBottom(true);
    }
  }, []);

  // Reset scroll state when document changes
  useEffect(() => {
    setHasScrolledToBottom(false);
    const el = contentRef.current;
    if (el) {
      el.scrollTop = 0;
      // If content is short enough to not require scrolling, auto-enable
      requestAnimationFrame(() => {
        if (el.scrollHeight <= el.clientHeight + 30) {
          setHasScrolledToBottom(true);
        }
      });
    }
  }, [currentIdx, currentDoc?.id]);

  // -----------------------------------------------------------------------
  // Accept handler
  // -----------------------------------------------------------------------

  const handleAccept = useCallback(async () => {
    if (!currentDoc) return;
    const success = await acceptDocument(currentDoc.id);
    if (success) {
      setAcceptedCount((prev) => prev + 1);
      // The hook removes the doc from pendingDocuments, so currentIdx stays 0
      setCurrentIdx(0);
    }
  }, [currentDoc, acceptDocument]);

  // -----------------------------------------------------------------------
  // Don't render if nothing pending
  // -----------------------------------------------------------------------

  if (loading || !hasPendingLegal) return null;
  if (!currentDoc) return null;

  const progress =
    totalDocuments > 0 ? (acceptedCount / totalDocuments) * 100 : 0;

  return (
    <Dialog
      open
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      // Prevent closing via backdrop click
      onClose={(_event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") return;
      }}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: "blur(8px)",
            backgroundColor: alpha(theme.palette.common.black, 0.6),
          },
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          // Same frosted navy card as every other dark dialog and as the
          // /environments, /billing and /superadmin surfaces.
          background: `linear-gradient(160deg, ${athenaColors.deepNavy} 0%, ${alpha(
            athenaColors.royalBlue,
            0.95
          )} 100%)`,
          backdropFilter: "blur(16px)",
          border: `1px solid ${alpha(athenaColors.periwinkle, 0.3)}`,
          boxShadow: `0 24px 80px ${alpha(
            athenaColors.deepNavy,
            0.5
          )}, 0 0 30px ${alpha(athenaColors.royalBlue, 0.2)}`,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ================================================================= */}
      {/* HEADER — translucent band over the card                          */}
      {/* ================================================================= */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          pb: 1.5,
          // Deliberately NOT its own gradient: the card behind it is already
          // one, so a second reads as a seam rather than a header.
          background: alpha(athenaColors.deepNavy, 0.5),
          borderBottom: `1px solid ${glass.headerDivider}`,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: alpha(athenaColors.periwinkle, 0.15),
            border: `1px solid ${alpha(athenaColors.periwinkle, 0.25)}`,
          }}
        >
          <LegalIcon sx={{ color: athenaColors.periwinkle, fontSize: 24 }} />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ lineHeight: 1.2, color: theme.palette.common.white }}
          >
            {t("legal.modal.title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ mt: 0.25, color: athenaColors.periwinkle }}
          >
            {t("legal.modal.subtitle")}
          </Typography>
        </Box>

        {/* Document counter */}
        {totalDocuments > 1 && (
          <Chip
            label={t("legal.modal.remaining", {
              current: String(acceptedCount + 1),
              total: String(totalDocuments),
            })}
            size="small"
            sx={{
            color: theme.palette.common.white,
            borderColor: alpha(athenaColors.periwinkle, 0.4),
              fontWeight: 600,
            }}
            variant="outlined"
          />
        )}
      </DialogTitle>

      {/* Progress bar for multi-document flows */}
      {totalDocuments > 1 && (
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 3,
            backgroundColor: alpha(athenaColors.royalBlue, 0.15),
            "& .MuiLinearProgress-bar": {
              backgroundColor: athenaColors.royalBlue,
            },
          }}
        />
      )}

      {/* ================================================================= */}
      {/* DOCUMENT METADATA — sunken strip                                 */}
      {/* ================================================================= */}
      <Box
        sx={{
          px: 3,
          pt: 1.5,
          pb: 1,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          background: glass.panelSunken,
          borderBottom: `1px solid ${glass.headerDivider}`,
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ flex: 1, color: glass.textPrimary }}
        >
          {currentDoc.title}
        </Typography>
        <Typography variant="caption" sx={{ color: glass.textSecondary }}>
          {t("legal.modal.version", { version: currentDoc.version })}
        </Typography>
        <Typography variant="caption" sx={{ color: glass.textSecondary }}>
          {t("legal.modal.effective_date", {
            date: new Date(currentDoc.effective_date).toLocaleDateString(),
          })}
        </Typography>
      </Box>

      {/* ================================================================= */}
      {/* SCROLLABLE CONTENT                                                */}
      {/* ================================================================= */}
      <DialogContent
        ref={contentRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 3,
          py: 2,
          // Transparent: the card itself is the surface.
          background: "transparent",
          // Custom scrollbar
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(athenaColors.white, 0.25),
            borderRadius: 3,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
        }}
      >
        <Box
          dangerouslySetInnerHTML={{
            __html: renderMarkdownToHtml(currentDoc.content),
          }}
          sx={{
            color: glass.textPrimary,
            fontSize: "0.92rem",
            lineHeight: 1.75,
            // renderMarkdownToHtml emits no colours, so these are the only
            // thing inking the agreement text.
            "& h2": {
              color: glass.accent,
            },
            "& h3": {
              color: glass.textPrimary,
            },
            "& ul": {
              color: glass.textSecondary,
            },
            "& p": {
              color: glass.textPrimary,
            },
            "& strong": {
              color: glass.textPrimary,
            },
          }}
        />
      </DialogContent>

      {/* ================================================================= */}
      {/* FOOTER                                                            */}
      {/* ================================================================= */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: alpha(athenaColors.deepNavy, 0.5),
          borderTop: `1px solid ${glass.headerDivider}`,
        }}
      >
        {/* Scroll hint */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            opacity: hasScrolledToBottom ? 0 : 0.8,
            transition: "opacity 0.3s ease",
          }}
        >
          <ScrollIcon
            fontSize="small"
            sx={{
              color: athenaColors.periwinkle,
              animation: hasScrolledToBottom ? "none" : "bounce 1.5s infinite",
              "@keyframes bounce": {
                "0%, 100%": { transform: "translateY(0)" },
                "50%": { transform: "translateY(4px)" },
              },
            }}
          />
          <Typography variant="caption" sx={{ color: athenaColors.periwinkle }}>
            {t("legal.modal.scroll_hint")}
          </Typography>
        </Box>

        {/* Accept button */}
        <Button
          variant="contained"
          disabled={!hasScrolledToBottom || accepting}
          onClick={handleAccept}
          startIcon={
            accepting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <AcceptedIcon />
            )
          }
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1,
            fontWeight: 600,
            textTransform: "none",
            fontSize: "0.95rem",
            // Was periwinkle (#b7c1de) with a WHITE label: ~1.75:1, the same
            // buried-ink problem as the rest of this pass. royalBlue + white is
            // the CTA used across the dark surfaces.
            backgroundColor: hasScrolledToBottom
              ? glass.ctaBg
              : alpha(athenaColors.white, 0.08),
            color: theme.palette.common.white,
            border: `1px solid ${
              hasScrolledToBottom ? glass.ctaBorder : "transparent"
            }`,
            // Force icon to inherit button text color
            "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "inherit" },
            boxShadow: hasScrolledToBottom
              ? `0 2px 12px ${alpha(athenaColors.royalBlue, 0.5)}`
              : "none",
            "&:hover": {
              backgroundColor: alpha(athenaColors.royalBlue, 0.85),
              borderColor: athenaColors.periwinkle,
            },
            // Must still read as PRESENT while disabled: it is the only way
            // out of a non-dismissible modal, so an invisible button looks
            // like a dead end rather than a "scroll first" hint.
            "&.Mui-disabled": {
              backgroundColor: alpha(athenaColors.white, 0.08),
              color: alpha(athenaColors.white, 0.45),
            },
            transition: "all 0.3s ease",
          }}
        >
          {accepting
            ? t("legal.modal.accepting")
            : t("legal.modal.accept_button")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
